import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, CheckCircle, AlertCircle, XCircle, Camera,
  Plus, X, Loader2, DollarSign, Clock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const APPROVAL_OPTIONS = [
  { value: "approved", label: "Aprovado", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { value: "approved_with_reservations", label: "Aprovado com Ressalvas", icon: AlertCircle, color: "bg-amber-100 text-amber-700 border-amber-300" },
  { value: "rejected", label: "Rejeitado", icon: XCircle, color: "bg-red-100 text-red-700 border-red-300" }
];

export default function ProjectMeasurementPage() {
  const { projectId, stageId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [stage, setStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    status: "",
    total_value: "",
    approved_value: "",
    retained_value: "",
    reservations: [],
    notes: "",
    evidence: []
  });

  const [newReservation, setNewReservation] = useState({
    item: "",
    description: "",
    deadline: ""
  });

  useEffect(() => {
    fetchStage();
  }, [projectId, stageId, token]);

  const fetchStage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const stages = await res.json();
        const currentStage = stages.find(s => s.stage_id === stageId);
        if (currentStage) {
          setStage(currentStage);
          setFormData(prev => ({
            ...prev,
            total_value: currentStage.estimated_cost?.toString() || ""
          }));
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        newPhotos.push(reader.result);
        if (newPhotos.length === files.length) {
          setFormData(prev => ({
            ...prev,
            evidence: [...prev.evidence, ...newPhotos]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddReservation = () => {
    if (!newReservation.item.trim()) {
      toast.error("Digite o item da ressalva");
      return;
    }
    setFormData(prev => ({
      ...prev,
      reservations: [...prev.reservations, { ...newReservation, resolved: false }]
    }));
    setNewReservation({ item: "", description: "", deadline: "" });
  };

  const handleRemoveReservation = (index) => {
    setFormData(prev => ({
      ...prev,
      reservations: prev.reservations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.status) {
      toast.error("Selecione o resultado da medição");
      return;
    }

    if (formData.status === "approved_with_reservations" && formData.reservations.length === 0) {
      toast.error("Adicione pelo menos uma ressalva");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/stages/${stageId}/measure`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          status: formData.status,
          total_value: parseFloat(formData.total_value) || 0,
          approved_value: parseFloat(formData.approved_value) || parseFloat(formData.total_value) || 0,
          retained_value: parseFloat(formData.retained_value) || 0,
          reservations: formData.reservations,
          notes: formData.notes,
          evidence: formData.evidence,
          correction_deadline: formData.reservations.length > 0 
            ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
            : null
        })
      });

      if (res.ok) {
        toast.success("Medição registrada com sucesso!");
        navigate(`/project/${projectId}/timeline`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erro ao registrar medição");
      }
    } catch (error) {
      toast.error("Erro ao registrar medição");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!stage) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <p className="text-neutral-500">Etapa não encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="project-measurement-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(`/project/${projectId}/timeline`)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Medição e Aceite</h1>
            <p className="text-xs text-neutral-500">{stage.name}</p>
          </div>
        </div>
      </header>

      <main className="px-5 py-6 space-y-6">
        {/* Stage Info */}
        <div className="card-subtle p-4 animate-up">
          <h3 className="font-semibold text-neutral-900 mb-2">Etapa {stage.order}: {stage.name}</h3>
          {stage.description && (
            <p className="text-sm text-neutral-600">{stage.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-neutral-500">
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              <span>R$ {stage.estimated_cost?.toLocaleString("pt-BR") || "0"}</span>
            </div>
          </div>
        </div>

        {/* Approval Status */}
        <div className="animate-up stagger-1">
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Resultado da Medição *
          </label>
          <div className="space-y-2">
            {APPROVAL_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setFormData(prev => ({ ...prev, status: option.value }))}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    formData.status === option.value
                      ? option.color
                      : "bg-white border-neutral-200 hover:border-neutral-300"
                  }`}
                  data-testid={`approval-${option.value}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Values */}
        <div className="space-y-4 animate-up stagger-2">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Valor Total da Etapa
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
              <input
                type="number"
                value={formData.total_value}
                onChange={(e) => setFormData(prev => ({ ...prev, total_value: e.target.value }))}
                className="input-minimal pl-12"
                placeholder="0,00"
              />
            </div>
          </div>

          {formData.status && formData.status !== "rejected" && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Valor Aprovado
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
                  <input
                    type="number"
                    value={formData.approved_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, approved_value: e.target.value }))}
                    className="input-minimal pl-12"
                    placeholder={formData.total_value || "0,00"}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Valor Retido (ressalvas)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
                  <input
                    type="number"
                    value={formData.retained_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, retained_value: e.target.value }))}
                    className="input-minimal pl-12"
                    placeholder="0,00"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Reservations (for approved with reservations) */}
        {formData.status === "approved_with_reservations" && (
          <div className="animate-up">
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Ressalvas
            </label>

            {/* Add Reservation Form */}
            <div className="card-subtle p-4 mb-4 space-y-3">
              <input
                type="text"
                value={newReservation.item}
                onChange={(e) => setNewReservation(prev => ({ ...prev, item: e.target.value }))}
                placeholder="Item (ex: Acabamento da parede)"
                className="input-minimal"
              />
              <textarea
                value={newReservation.description}
                onChange={(e) => setNewReservation(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição do problema..."
                className="input-minimal h-20 resize-none py-3"
              />
              <input
                type="date"
                value={newReservation.deadline}
                onChange={(e) => setNewReservation(prev => ({ ...prev, deadline: e.target.value }))}
                className="input-minimal"
              />
              <Button
                onClick={handleAddReservation}
                className="btn-minimal btn-secondary w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Ressalva
              </Button>
            </div>

            {/* Reservations List */}
            {formData.reservations.length > 0 && (
              <div className="space-y-2">
                {formData.reservations.map((res, index) => (
                  <div key={index} className="card-clean p-3 flex items-start justify-between">
                    <div>
                      <p className="font-medium text-neutral-900">{res.item}</p>
                      {res.description && (
                        <p className="text-sm text-neutral-500 mt-1">{res.description}</p>
                      )}
                      {res.deadline && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-amber-600">
                          <Clock className="w-3 h-3" />
                          <span>Prazo: {new Date(res.deadline).toLocaleDateString("pt-BR")}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveReservation(index)}
                      className="p-1 hover:bg-neutral-100 rounded"
                    >
                      <X className="w-4 h-4 text-neutral-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="animate-up">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Observações
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Observações adicionais..."
            className="input-minimal h-24 resize-none py-3"
          />
        </div>

        {/* Evidence Photos */}
        <div className="animate-up">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Evidências Fotográficas
          </label>
          <div className="flex gap-2 flex-wrap">
            {formData.evidence.map((photo, index) => (
              <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    evidence: prev.evidence.filter((_, i) => i !== index)
                  }))}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-neutral-300 flex items-center justify-center hover:border-neutral-400 transition-colors"
            >
              <Camera className="w-6 h-6 text-neutral-400" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !formData.status}
            className="btn-minimal btn-primary w-full"
            data-testid="submit-measurement-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Registrar Medição"
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
